import axios from 'axios';
import { Module } from 'vuex';
import Vue from 'vue';

import {
	AlmacenesState,
	PedidoPendiente,
	PedidoPendienteAPI,
	FetchPendientesResponse,
	FetchRenglonesResponse,
	RenglonAPI,
	Renglon,
	RootState,
	CatalogItemAPI,
	CatalogItem,
} from '@/models/StateAlmacenes';

const CATALOGO_URL = './api/public/proxy/cgi/catalogo';
const CGI_URL = './api/public/proxy/cgi/pedidosPendientes';
const RENGLONES_URL = './api/public/proxy/cgi/renglonesPedido';
// const TOKEN = 'd9eb7739b41b2ce72e0bca2c0aaee9c6b32e8826';
let inflightCatalogAbort: AbortController | null = null;

const state = (): AlmacenesState => ({
	tableData: [],
	loading: false,
	error: null,

	linesByNumero: {},
	linesLoading: {},
	linesError: {},

	catalogResults: [] as CatalogItem[],
	catalogLoading: false,
	catalogError: null as string | null,
});

const getters = {
	tableData: (s: AlmacenesState) => s.tableData,
	isLoading: (s: AlmacenesState) => s.loading,
	error: (s: AlmacenesState) => s.error,

	// renglones por pedido
	orderLines: (s: AlmacenesState) => (numero: number): Renglon[] => (s.linesByNumero && s.linesByNumero[numero]) || [],
	// renglones visibles por pedido
	visibleOrderLines: (s: AlmacenesState, g: any) => (numero: number): Renglon[] =>
		g.orderLines(numero).filter((r: Renglon) => Number(r.cantidad) > 0),
	// isLoading de renglones por pedido
	isLinesLoading: (s: AlmacenesState) => (numero: number): boolean => !!s.linesLoading && s.linesLoading[numero],
	// error de renglones por pedido
	linesError: (s: AlmacenesState) => (numero: number): string | null => (s.linesError && s.linesError[numero]) || null,
	// catálogo
	catalogResults: (s: AlmacenesState) => (s as any).catalogResults as CatalogItem[],
	// renglones cargados
	isCatalogLoading: (s: AlmacenesState) => (s as any).catalogLoading as boolean,
	catalogError: (s: AlmacenesState) => (s as any).catalogError as string | null,
};

const mutations = {
	REPLACE_LINE_PRODUCT(state: AlmacenesState, { numero, id, item }: { numero: number; id: string | number; item: CatalogItem }) {
		if (!state.linesByNumero) state.linesByNumero = {};
		const list = state.linesByNumero[numero] || [];
		const idx = list.findIndex(l => l.id === id);
		if (idx === -1) return;

		const old = list[idx];
		const updated: Renglon = {
			...old,
			codalfa: item.codalfa,
			alias: item.alias,
			nombre: item.nombre,
			linea: item.linea,
			marca: item.marca,
			ubicacion: item.ubicacion,
			precio: item.precio,
		};

		Vue.set(list, idx, updated);
	},
	RESET(s: AlmacenesState) {
		s.tableData = [];
		s.error = null;
	},
	SET_LOADING(s: AlmacenesState, flag: boolean) {
		s.loading = flag;
	},
	SET_ERROR(s: AlmacenesState, msg: string | null) {
		s.error = msg;
	},
	SET_TABLE_DATA(s: AlmacenesState, rows: PedidoPendiente[]) {
		s.tableData = rows || [];
	},
	SET_LINE_CANTIDAD(state: AlmacenesState, { numero, id, cantidad }: { numero: number; id: string | number; cantidad: number }) {
		if (!state.linesByNumero) state.linesByNumero = {};
		const list = state.linesByNumero[numero] || [];
		const idx = list.findIndex(l => l.id === id);
		if (idx !== -1) {
			// asegurar reactividad
			Vue.set(list[idx], 'cantidad', cantidad);
		}
	},
	// 👇 Renglones por pedido
	SET_LINES_LOADING(state, { numero, flag }: { numero: number; flag: boolean }) {
		if (!state.linesLoading) state.linesLoading = {};
		Vue.set(state.linesLoading, numero, flag);
	},

	// Error al cargar renglones por pedido
	SET_LINES_ERROR(state, { numero, msg }: { numero: number; msg: string | null }) {
		if (!state.linesError) state.linesError = {};
		Vue.set(state.linesError, numero, msg);
	},

	// Reemplaza todos los renglones de un pedido
	SET_ORDER_LINES(state, { numero, lines }: { numero: number; lines: Renglon[] }) {
		if (!state.linesByNumero) state.linesByNumero = {};
		Vue.set(state.linesByNumero, numero, lines);
	},

	// 👇 Catálogo
	SET_CATALOG_LOADING(s: AlmacenesState, flag: boolean) {
		(s as any).catalogLoading = flag;
	},
	SET_CATALOG_ERROR(s: AlmacenesState, msg: string | null) {
		(s as any).catalogError = msg;
	},
	SET_CATALOG_RESULTS(s: AlmacenesState, results: CatalogItem[]) {
		(s as any).catalogResults = results || [];
	},

	// agrega un renglón localmente a un pedido
	ADD_LINE_LOCAL(s: AlmacenesState, payload: { numero: number; line: Renglon }) {
		if (!s.linesByNumero) s.linesByNumero = {};
		const current = s.linesByNumero[payload.numero] || [];
		const next = current.concat(payload.line);
		Vue.set(s.linesByNumero, payload.numero, next);
	},
};

const actions = {
	reset({ commit }) {
		commit('RESET');
	},
	replaceProductoLocal({ commit }, { numero, id, item }: { numero: number; id: string | number; item: CatalogItem }) {
		commit('REPLACE_LINE_PRODUCT', { numero, id, item });
	},
	async fetchPendientes({ commit }) {
		commit('SET_LOADING', true);
		commit('SET_ERROR', null);
		try {
			// const http = axios.create();
			/*const { data } = await http.get<FetchPendientesResponse>(CGI_URL, {
				headers: {
					Authorization: `Bearer ${TOKEN}`,
					Accept: 'application/json',
				},
			});*/
			const data = await axios.get(CGI_URL);

			const rows: PedidoPendiente[] = (data.data.items || []).map((it: PedidoPendienteAPI) => ({
				id: it.numero,
				numero: it.numero,
				razsocial: (it.razsoc || '').trim(),
				fecha: it.fecha,
				vendedor: it.vendedor,
				codent: it.codent,
				observacion: it.observacion,
			}));

			commit('SET_TABLE_DATA', rows);
		} catch (error) {
			console.log('Error al cargar pendientes');
		} finally {
			commit('SET_LOADING', false);
		}
	},
	async fetchRenglonesPorPedido({ commit }, numero: number) {
		commit('SET_LINES_LOADING', { numero, flag: true });
		commit('SET_LINES_ERROR', { numero, msg: null });
		try {
			// const http = axios.create();
			const { data } = await axios.get(`${RENGLONES_URL}?numero=${encodeURIComponent(numero)}`);
			// const { data } = await http.get<FetchRenglonesResponse>(`${RENGLONES_URL}?numero=${encodeURIComponent(numero)}`, {
			// headers: {
			// 	Authorization: `Bearer ${TOKEN}`,
			// 	Accept: 'application/json',
			// },
			// });

			const lines: Renglon[] = (data.items || []).map((r: RenglonAPI) => ({
				id: r.nroren,
				nroren: r.nroren,
				codalfa: r.codalfa,
				alias: r.alfacor,
				nombre: r.producto,
				linea: r.linea,
				marca: r.marca,
				ubicacion: r.ubicacion,
				cantidad: r.cantidad,
				precio: Number(r.precio || r.precvta || 0),
				movprec: Number(r.movprec || 0),
			}));

			commit('SET_ORDER_LINES', { numero, lines });
		} catch (e) {
			commit('SET_LINES_ERROR', { numero, msg: 'Error al cargar renglones' });
		} finally {
			commit('SET_LINES_LOADING', { numero, flag: false });
		}
	},
	// buscar productos (catalogo)
	async buscarCatalogo({ commit }, { descri, codemp = 1, iduser = 8888 }: { descri: string; codemp?: number; iduser?: number }) {
		const q = (descri || '').trim();
		if (q.length < 3) {
			// doble guard del lado store
			commit('SET_CATALOG_RESULTS', []);
			return;
		}

		commit('SET_CATALOG_LOADING', true);
		commit('SET_CATALOG_ERROR', null);

		// cancelá la request anterior si sigue viva
		try {
			if (inflightCatalogAbort) inflightCatalogAbort.abort();
		} catch {}
		const controller = new AbortController();
		inflightCatalogAbort = controller;

		try {
			// const http = axios.create();

			const { data } = await axios.post(
				CATALOGO_URL,
				{ codemp, iduser, descri: q },
				{
					// {
					// headers: {
					// 	Authorization: `Bearer ${TOKEN}`,
					// 	Accept: 'application/json',
					// 	'Content-Type': 'application/json',
					// },
					cancelToken: new axios.CancelToken(cancel => {
						controller.signal.addEventListener('abort', () => cancel('Request aborted'));
					}),
				}
			);

			const results: CatalogItem[] = (data.items || []).map((it: CatalogItemAPI) => ({
				id: it.codalfa_art || it.codalfa,
				codalfa: it.codalfa,
				alias: it.alfacor,
				nombre: it.producto,
				linea: it.linea,
				marca: it.marca,
				ubicacion: it.ubicacion,
				precio: Number(it.precvta || 0),
				stock: Number(it.stock || 0),
				fotoUrl: it.fotoUrl && it.fotoUrl[0],
			}));

			commit('SET_CATALOG_RESULTS', results);
		} catch (err) {
			if (err instanceof axios.Cancel) return;
			commit('SET_CATALOG_ERROR', 'Error al buscar productos');
			commit('SET_CATALOG_RESULTS', []);
		} finally {
			commit('SET_CATALOG_LOADING', false);
			inflightCatalogAbort = null;
		}
	},

	// agregar localmente un producto al pedido actual
	addProductoLocal({ commit }, { numero, item, cantidad }: { numero: number; item: CatalogItem; cantidad: number }) {
		const line: Renglon = {
			id: Date.now(), // id local temporal
			nroren: 9999,
			codalfa: item.codalfa,
			alias: item.alias,
			nombre: item.nombre,
			linea: item.linea,
			marca: item.marca,
			ubicacion: item.ubicacion,
			cantidad,
			precio: item.precio,
			movprec: item.precio || 0,
		};
		commit('ADD_LINE_LOCAL', { numero, line });
	},

	async confirmarPedido(
		{ getters },
		payload: {
			numero: number;
			codemp: number;
			codcom: number;
			codtal: number;
			usrid: string;
		}
	) {
		const { numero, codemp, codcom, codtal, usrid } = payload;
		// construir cabecera y renglones en el formato legacy que espera el CGI
		const buildCabecera = () => [{ codemp, codcom, codtal, numero, usrid }];

		// transforma renglones al formato legacy que espera el CGI
		const buildRenglonesLegacy = () => {
			const all: Renglon[] = getters.orderLines(numero) || [];
			return all.map((r: Renglon) => {
				const nroren = (r as any).nroren !== undefined && (r as any).nroren !== null ? (r as any).nroren : 9999;
				return {
					nroren,
					alfacor: r.alias !== undefined && r.alias !== null ? r.alias : null,
					cantidad: Number(r.cantidad),
					codalfa: r.codalfa !== undefined && r.codalfa !== null ? r.codalfa : null,
					marca: r.marca !== undefined && r.marca !== null ? r.marca : null,
					movprec: r.movprec !== undefined && (r as any).movprec !== null ? (r as any).movprec : 0,
					producto: r.nombre,
					ubicacion: r.ubicacion !== undefined && r.ubicacion !== null ? r.ubicacion : null,
				};
			});
		};

		const cabecera = buildCabecera();
		const renglones = buildRenglonesLegacy();
		const doCancelarPedido = renglones.every(r => Number(r.cantidad) === 0);

		// Manda JSON ENCODADO como string
		const bodyPut = {
			cabecera: JSON.stringify(cabecera),
			renglones: JSON.stringify(renglones),
		};

		// const http = axios.create({
		// 	headers: {
		// 		Authorization: `Bearer ${TOKEN}`,
		// 		Accept: 'application/json',
		// 		'Content-Type': 'application/json',
		// 	},
		// });

		const putRes = await axios.post('./api/public/proxy/cgi/putPicking', bodyPut);

		if (!putRes || !putRes.data || !putRes.data.success) throw new Error('PUT picking no exitoso');

		if (doCancelarPedido) {
			const bodyCancel = {
				codemp,
				codcom,
				codtal,
				numero,
				motivo: 1,
				usrid,
			};
			const cancelRes = await axios.post('./api/public/proxy/cgi/cancelarPedido', bodyCancel);
			if (!cancelRes || !cancelRes.data || !cancelRes.data.success) throw new Error('Cancelación no exitosa');
		}

		return true;
	},
};

const almacenes: Module<AlmacenesState, RootState> = {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};

export default almacenes;
